import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Edit, Trash2, BookOpen, Video, Users, Target, Clock } from 'lucide-react';
import { toast } from 'sonner';

function HomeworkManager({ token }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    chapter_id: 1,
    assignment_type: 'reading',
    title: '',
    description: '',
    content: '',
    instructions: '',
    page_reference: '',
    estimated_time: '',
    required: true
  });

  const assignmentTypes = [
    { value: 'reading', label: 'Reading Assignment', icon: BookOpen, color: 'bg-blue-500' },
    { value: 'video', label: 'Video Practice', icon: Video, color: 'bg-purple-500' },
    { value: 'partner', label: 'Partner Exercise', icon: Users, color: 'bg-green-500' },
    { value: 'practice', label: 'Daily Practice', icon: Target, color: 'bg-orange-500' },
    { value: 'exercise', label: 'Written Exercise', icon: Edit, color: 'bg-pink-500' }
  ];

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/homework`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const saveAssignment = async (assignment) => {
    try {
      const url = assignment.id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/homework/${assignment.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/homework`;
      
      const response = await fetch(url, {
        method: assignment.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignment)
      });

      if (!response.ok) throw new Error('Save failed');

      toast.success(`Assignment ${assignment.id ? 'updated' : 'created'} successfully!`);
      setEditingAssignment(null);
      setNewAssignment({
        chapter_id: 1,
        assignment_type: 'reading',
        title: '',
        description: '',
        content: '',
        instructions: '',
        page_reference: '',
        estimated_time: '',
        required: true
      });
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to save assignment');
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (window.confirm('Delete this assignment?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/homework/${assignmentId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Delete failed');

        toast.success('Assignment deleted');
        fetchAssignments();
      } catch (error) {
        toast.error('Failed to delete assignment');
      }
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">📝 Homework Assignment Manager</h3>
          <p className="text-gray-600">Create and manage Home Practice assignments for each chapter</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-green-600 text-white hover:bg-green-700" style={{ borderRadius: 0 }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 0 }}>
            <DialogHeader>
              <DialogTitle>Create Homework Assignment</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveAssignment(newAssignment); }} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Chapter</Label>
                  <select
                    value={newAssignment.chapter_id}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, chapter_id: parseInt(e.target.value) }))}
                    className="w-full p-3 border-2 border-black"
                    style={{ borderRadius: 0 }}
                  >
                    {[1,2,3,4,5,6,7,8].map(num => (
                      <option key={num} value={num}>Chapter {num}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Assignment Type</Label>
                  <select
                    value={newAssignment.assignment_type}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, assignment_type: e.target.value }))}
                    className="w-full p-3 border-2 border-black"
                    style={{ borderRadius: 0 }}
                  >
                    {assignmentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label>Title</Label>
                <Input
                  value={newAssignment.title}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Read Chapter 4 in NVC Book"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>
              
              <div>
                <Label>Description (Brief Summary)</Label>
                <Input
                  value={newAssignment.description}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Short description of the assignment"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <div>
                <Label>Full Content (Paste from PDF here)</Label>
                <Textarea
                  value={newAssignment.content}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Paste the complete homework content from the PDF here - be as detailed as needed"
                  className="border-black min-h-[200px]"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Page Reference (Optional)</Label>
                  <Input
                    value={newAssignment.page_reference}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, page_reference: e.target.value }))}
                    placeholder="e.g., Pages 93-98"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Estimated Time</Label>
                  <Input
                    value={newAssignment.estimated_time}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, estimated_time: e.target.value }))}
                    placeholder="e.g., 15-20 minutes"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
              
              <div>
                <Label>Instructions for Students</Label>
                <Textarea
                  value={newAssignment.instructions}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Any special instructions or context for students"
                  className="border-black min-h-[80px]"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="required"
                  checked={newAssignment.required}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, required: e.target.checked }))}
                />
                <Label htmlFor="required">Required Assignment</Label>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-black text-white hover:bg-black/90 py-4"
                style={{ borderRadius: 0 }}
              >
                Create Assignment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Assignments by Chapter */}
      <div className="space-y-8">
        {[1,2,3,4,5,6,7,8].map(chapterNum => {
          const chapterAssignments = assignments.filter(a => a.chapter_id === chapterNum);
          return (
            <Card key={chapterNum} style={{ borderRadius: 0 }} className="border-2 border-orange-500">
              <CardHeader>
                <CardTitle className="text-xl text-orange-800">
                  Chapter {chapterNum} - Home Practice ({chapterAssignments.length} assignments)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chapterAssignments.length === 0 ? (
                  <p className="text-gray-500 italic py-8 text-center">
                    No assignments yet. Click "Add Assignment" to create homework for this chapter.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {chapterAssignments.map((assignment) => {
                      const typeConfig = assignmentTypes.find(t => t.value === assignment.assignment_type);
                      const IconComponent = typeConfig?.icon || BookOpen;
                      
                      return (
                        <Card key={assignment.id} className="border border-gray-300" style={{ borderRadius: 0 }}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className={`p-2 rounded ${typeConfig?.color} text-white`}>
                                    <IconComponent className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-gray-800">{assignment.title}</h4>
                                    <p className="text-sm text-gray-600">{assignment.description}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2 mb-3">
                                  <Badge className="bg-orange-500 text-white">{typeConfig?.label}</Badge>
                                  {assignment.page_reference && (
                                    <Badge variant="outline">{assignment.page_reference}</Badge>
                                  )}
                                  {assignment.estimated_time && (
                                    <Badge variant="outline">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {assignment.estimated_time}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700 line-clamp-3">
                                  {assignment.content}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => setEditingAssignment(assignment)}
                                  variant="outline"
                                  size="sm"
                                  className="border-blue-500 text-blue-600"
                                  style={{ borderRadius: 0 }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  onClick={() => deleteAssignment(assignment.id)}
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500 text-red-600"
                                  style={{ borderRadius: 0 }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Add Templates */}
      <Card className="border-4 border-green-500 bg-green-50" style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-xl text-green-800">🚀 Quick Templates</CardTitle>
          <p className="text-green-700">Copy and paste content from the PDF for each chapter</p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {[1,2,3,4,5,6,7,8].map(chapterNum => (
              <Button
                key={chapterNum}
                onClick={() => {
                  setNewAssignment(prev => ({
                    ...prev,
                    chapter_id: chapterNum,
                    title: `Chapter ${chapterNum} Homework`,
                    description: `Home practice assignments for Chapter ${chapterNum}`
                  }));
                  toast.success(`Template ready for Chapter ${chapterNum}! Paste PDF content in the form.`);
                }}
                variant="outline"
                className="border-green-500 text-green-600 py-6"
                style={{ borderRadius: 0 }}
              >
                📖 Chapter {chapterNum} Template
              </Button>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500">
            <h4 className="font-bold text-yellow-800 mb-2">💡 How to Add PDF Content:</h4>
            <ol className="text-sm text-yellow-700 space-y-1">
              <li>1. Click "Add Assignment" above</li>
              <li>2. Select chapter and assignment type</li>
              <li>3. Copy homework content directly from the PDF</li>
              <li>4. Paste into "Full Content" field</li>
              <li>5. Students will see your exact content in their workbook!</li>
            </ol>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

export default HomeworkManager;